import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { MOTION } from '../../../lib/immobilien/motion';

/**
 * Bestätigungs-Pille in Panels/Formularen (README §9: Pille --ok-soft, 38 px,
 * verschwindet nach ~2,5 s). Für app-weite Meldungen bleibt
 * NotificationContext.showToast zuständig – das hier ist nur die Inline-Anzeige.
 */
export interface ToastAktion { label: string; onClick: () => void }

export function ToastPill({ text, fehler = false, aktion }: { text: string; fehler?: boolean; aktion?: ToastAktion }) {
  const IconKomp = fehler ? WarningCircle : CheckCircle;
  return (
    <div
      role="status"
      aria-live="polite"
      className="immo-side-in inline-flex items-center self-start"
      style={{
        height: 38, padding: '0 16px', borderRadius: 19, gap: 8, fontSize: 13, fontWeight: 700,
        background: fehler ? 'var(--bad-soft)' : 'var(--ok-soft)', color: fehler ? 'var(--bad)' : 'var(--ok)',
      }}
    >
      <IconKomp weight="fill" size={16} style={{ flexShrink: 0 }} />
      <span className="min-w-0" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
      {aktion && (
        <button type="button" onClick={aktion.onClick} className="cursor-pointer shrink-0"
          style={{ height: 32, padding: '0 12px', marginRight: -10, borderRadius: 16, border: 'none', background: 'var(--acc-soft)', color: 'var(--acc)', fontSize: 12, fontWeight: 800 }}>
          {aktion.label}
        </button>
      )}
    </div>
  );
}

export interface ToastZustand { text: string; fehler: boolean; aktion?: ToastAktion }

/** Lokaler Toast mit automatischem Ausblenden. */
export function useToastPill(dauerMs: number = MOTION.toastMs): [ToastZustand | null, (text: string, fehler?: boolean, aktion?: ToastAktion) => void] {
  const [toast, setToast] = useState<ToastZustand | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const zeige = useCallback((text: string, fehler = false, aktion?: ToastAktion) => {
    if (timer.current) clearTimeout(timer.current);
    // Mit Aktion (Rückgängig) bleibt der Toast länger stehen; ein Klick darauf schließt ihn.
    setToast({ text, fehler, aktion: aktion && { label: aktion.label, onClick: () => { setToast(null); aktion.onClick(); } } });
    timer.current = setTimeout(() => setToast(null), aktion ? Math.max(dauerMs, 6000) : dauerMs);
  }, [dauerMs]);

  return [toast, zeige];
}

/** Toast schwebend unten mittig (über der mobilen Leiste), deckend hinterlegt. */
export function SchwebenderToast({ toast, mobil = false }: { toast: ToastZustand | null; mobil?: boolean }) {
  if (!toast) return null;
  return (
    <div className="fixed flex justify-center" style={{ left: 16, right: 16, bottom: mobil ? 96 : 24, zIndex: 420, pointerEvents: 'none' }}>
      <div className="flex" style={{ pointerEvents: 'auto', maxWidth: '100%', boxShadow: '0 12px 30px -12px var(--shade)', borderRadius: 19, background: 'var(--bg)' }}>
        <ToastPill text={toast.text} fehler={toast.fehler} aktion={toast.aktion} />
      </div>
    </div>
  );
}
